import { MutationConfig, useNotifications } from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api-client';

import { getItemTemplateSchemaQueryKey } from './get-item-template-schema';
import { getItemTemplateSchemasQueryKey } from './get-item-template-schemas';
import { getItemTemplatesQueryKey } from './get-item-templates';

export const deleteItemTemplate = async (orgId: string, itemTemplateId: string): Promise<void> => {
  await api.delete(`/orgs/${orgId}/item-templates/${itemTemplateId}`);
};

interface UseDeleteItemTemplateOptions {
  mutationConfig?: MutationConfig<(itemTemplateId: string) => Promise<void>>;
}

export const useDeleteItemTemplate = ({ mutationConfig }: UseDeleteItemTemplateOptions = {}) => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const { orgId } = useScopeIds('org');

  const { onSuccess, onError, ...restConfig } = mutationConfig ?? {};

  return useMutation({
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: getItemTemplatesQueryKey(orgId) });
      void queryClient.invalidateQueries({ queryKey: getItemTemplateSchemasQueryKey(orgId) });
      void queryClient.invalidateQueries({ queryKey: getItemTemplateSchemaQueryKey(orgId) });

      notifications.success('itemTemplates.delete.success');
      onSuccess?.(...args);
    },
    onError: (...args) => {
      notifications.error('itemTemplates.delete.error');
      onError?.(...args);
    },
    ...restConfig,
    mutationFn: (itemTemplateId: string) => deleteItemTemplate(orgId, itemTemplateId),
  });
};
